import './ServiceOrderForm.scss';

import { DateField, TextField } from '@refinedev/antd';
import { Card, Form, FormProps, message, Tag } from 'antd';
import { type Dispatch, type SetStateAction, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ServiceOrderStatusTag from '@/components/serviceOrder/ServiceOrderStatusTag';
import { useCountryContext } from '@/contexts/CountryContext';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { SERVICE_ORDER_STATUS } from '@/model/ServiceOrder/ServiceOrder';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
// Mejora: se reemplazó el onFinishFailed inline por createFinishFailedHandler genérico
import { createFinishFailedHandler } from '@/utils/formHelpers';

import ServiceOrderItems from '../ServiceOrderItems/ServiceOrderItems';
import ServiceOrderAdminData from './ServiceOrderAdminData';
import ServiceOrderBillingData from './ServiceOrderBillingData';
import ServiceOrderServiceDetail from './ServiceOrderServiceDetail';
import useServiceOrderForm from './useServiceOrderForm';

interface ServiceOrderFormProps {
  formProps: FormProps;
  serviceOrderItems: ServiceOrderItem[];
  // Permite updates funcionales para evitar sobreescrituras.
  setServiceOrderItems: Dispatch<SetStateAction<ServiceOrderItem[]>>;
  onInvalidDistributionChange?: (hasInvalid: boolean) => void;
  // Optional status override (one-shot) coming from the edit page.
  statusOverrideRef?: { current: SERVICE_ORDER_STATUS | null };
}

const ServiceOrderForm = (props: ServiceOrderFormProps) => {
  const { userWorkArea } = useUserContext();
  const { isBrasilSelected } = useCountryContext();
  const {
    serviceOrderItems,
    setServiceOrderItems,
    onInvalidDistributionChange,
    statusOverrideRef,
  } = props;
  const { t } = useTranslation();
  const {
    formInitialValues,
    technicianSelectProps,
    companySelectProps,
    ticketsSelectProps,
    currencySelectProps,
    ticketMachineOptions,
    ticketMachineOptionsLoading,
    companyContactsSelectProps,
    companyContactRules,
    assignTechnicianPrice,
    clearDraft,
    createdAutomatically,
    companyInscripcionEstadual,
    companyRepository,
    technicianPaymentMethodsSelectProps,
    createPaymentMethod,
  } = useServiceOrderForm(props.formProps);
  const coordinationPending = useMemo(
    () => formInitialValues?.status === SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION,
    [formInitialValues?.status],
  );
  const initialTickets = useMemo(
    () => props.formProps?.initialValues?.tickets.map(ticket => ticket.id) ?? [],
    [props.formProps?.initialValues?.tickets],
  );
  const formProps = useMemo(
    () => ({ ...props.formProps, initialValues: formInitialValues }),
    [props.formProps, formInitialValues],
  );
  const selectedCompany = Form.useWatch('company', formProps.form);
  // Handler estabilizado para evitar re-render del Form en cada render del padre.
  const handleFinishFailed = useMemo(
    () =>
      createFinishFailedHandler(t, () => {
        if (statusOverrideRef) {
          statusOverrideRef.current = null;
        }
      }),
    [t, statusOverrideRef],
  );
  const handleFinish = useCallback(
    (v: any) => {
      if (!v.moviles || v.moviles.length === 0) {
        message.error(
          t('El ticket asociado a la orden de servicio debe tener al menos un móvil asignado'),
        );

        return;
      }

      const statusOverride = statusOverrideRef?.current;

      if (
        statusOverride !== null &&
        statusOverride !== undefined &&
        statusOverride !== SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION
      ) {
        v.status = statusOverride;
        formProps.form?.setFieldValue('status', statusOverride);
        if (statusOverrideRef) {
          statusOverrideRef.current = null;
        }
      }

      if (v.status === SERVICE_ORDER_STATUS.COORDINADA && v.service_date == null) {
        formProps.form?.setFields([
          {
            name: 'service_date',
            errors: [t('La fecha de servicio es requerida para coordinar una orden de servicio')],
          },
        ]);
        formProps.form?.scrollToField('service_date');

        return;
      }

      // Patch de inscripción estadual si el usuario la modificó respecto al valor de la empresa.
      if (
        v.company &&
        v.inscripcion_estadual &&
        companyInscripcionEstadual !== v.inscripcion_estadual
      ) {
        companyRepository.updateCompany(v.company, {
          inscripcion_estadual: v.inscripcion_estadual,
        });
      }

      clearDraft();

      formProps.onFinish?.(v);
    },
    [t, statusOverrideRef, formProps, clearDraft, companyInscripcionEstadual, companyRepository],
  );

  return (
    <Form
      {...formProps}
      className="ServiceOrderForm"
      layout="vertical"
      onFinishFailed={handleFinishFailed}
      onFinish={handleFinish}>
      {props.formProps?.initialValues && (
        <div className="ServiceOrderFormInitialDataSection">
          <div className="ServiceOrderFormInitialData ServiceOrderFormDataId">
            <b>
              {t('Orden de servicio')} #{formProps.initialValues?.id}
            </b>
            {formProps.initialValues?.status != undefined && (
              <div className="ServiceOrderFormRowBooleans">
                <ServiceOrderStatusTag serviceOrderStatus={formProps.initialValues.status} />
                {createdAutomatically && <Tag color="geekblue">{t('Automática')}</Tag>}
              </div>
            )}
          </div>

          <div className="ServiceOrderFormInitialData">
            <div>
              <b>{t('Fecha de creación')}: </b>
              <DateField value={formProps.initialValues?.created_at} />
            </div>
            <div>
              <b>{t('Fecha última modificación')}: </b>
              <DateField value={formProps.initialValues?.updated_at} />
            </div>

            <div>
              <b>{t('Creado por')}: </b>
              <TextField
                value={
                  formProps?.initialValues?.created_by
                    ? `${formProps?.initialValues?.created_by_str} (${formProps?.initialValues?.created_by})`
                    : ''
                }
              />
            </div>
          </div>
        </div>
      )}
      <Card className="ServiceOrderFormSection">
        <div className="ServiceOrderFormTitle">{t('Detalles del servicio')}</div>
        <ServiceOrderServiceDetail
          formProps={formProps}
          selectedCompany={selectedCompany}
          technicianSelectProps={technicianSelectProps}
          companySelectProps={companySelectProps}
          ticketsSelectProps={ticketsSelectProps}
          initialTickets={initialTickets}
          ticketMachineOptions={ticketMachineOptions}
          ticketMachineOptionsLoading={ticketMachineOptionsLoading}
          companyContactsSelectProps={companyContactsSelectProps}
          companyContactRules={companyContactRules}
          createdAutomatically={createdAutomatically}
          companyInscripcionEstadual={companyInscripcionEstadual}
        />
      </Card>
      {!coordinationPending && (
        <>
          <Card className="ServiceOrderFormSection">
            <div className="ServiceOrderFormTitle">{t('Detalles de la facturación')}</div>
            <ServiceOrderBillingData
              formProps={formProps}
              currencySelectProps={currencySelectProps}
              assignTechnicianPrice={assignTechnicianPrice}
              paymentMethodSelectProps={technicianPaymentMethodsSelectProps}
              onCreatePaymentMethod={createPaymentMethod}
            />
          </Card>
          {(userWorkArea === USER_WORK_AREA.ADMINISTRATION ||
            userWorkArea === USER_WORK_AREA.SOFTWARE) && (
            <Card className="ServiceOrderFormSection">
              <div className="ServiceOrderFormTitle">{t('Detalles administración')}</div>
              <ServiceOrderAdminData hideTechnicianReported={isBrasilSelected} />
            </Card>
          )}
          <Card>
            <div className="ServiceOrderFormTitle">{t('Elementos utilizados')}</div>
            <ServiceOrderItems
              formProps={formProps}
              serviceOrderItems={serviceOrderItems}
              setServiceOrderItems={setServiceOrderItems}
              onInvalidDistributionChange={onInvalidDistributionChange}
            />
          </Card>
        </>
      )}
    </Form>
  );
};

export default ServiceOrderForm;
